import { formatPickupPointAddress, formatPickupPointTitle, pickupPointIdentifier } from './pickupPoint.js'
import { getRwdPickupFieldSelector } from './adapters/rwdSelectors.js'

const WIDGET_CLASS = 'octolize_dhl_rwd_widget'
const MODAL_ID = 'octolize-dhl-rwd-map'
const STYLE_ID = 'octolize-dhl-rwd-styles'

const styles = `
  .${WIDGET_CLASS}{margin-top:8px}.${WIDGET_CLASS} [hidden]{display:none!important}.octolize_dhl_rwd_button{cursor:pointer}
  .octolize_dhl_rwd_details{margin-top:6px;font-size:13px;line-height:1.4}
  .octolize_dhl_rwd_name{font-weight:600}.octolize_dhl_rwd_postnummer{display:block;margin-top:8px}
  .octolize_dhl_rwd_postnummer[hidden],.octolize_dhl_rwd_details[hidden]{display:none}
  #${MODAL_ID}{position:fixed;inset:0;background:rgba(0,0,0,.8);z-index:100000}
  #${MODAL_ID}[hidden]{display:none}#${MODAL_ID} iframe{position:absolute;inset:8% 10%;width:80%;height:84%;border:0}
  #${MODAL_ID} button{position:absolute;right:24px;top:16px;z-index:1}
  .octolize_dhl_rwd_error{margin:8px 0;color:#b91c1c;font-weight:600}
`

function createWidget(root) {
  const widget = root.createElement('div')
  widget.className = WIDGET_CLASS
  widget.innerHTML = '<h4 class="separator octolize_dhl_rwd_heading"></h4><button type="button" class="btn btn_primary btn_s octolize_dhl_rwd_button">Wybierz punkt</button><div class="octolize_dhl_rwd_details" hidden><div class="octolize_dhl_rwd_name"></div><div class="octolize_dhl_rwd_address"></div></div><label class="octolize_dhl_rwd_postnummer" hidden>Postnummer * <input class="octolize_dhl_rwd_postnummer_input" type="text" inputmode="numeric" autocomplete="off"></label><div class="octolize_dhl_rwd_postnummer_summary" hidden></div><p class="octolize_dhl_rwd_summary_hint" hidden>Punkt możesz zmienić po powrocie do danych dostawy.</p><div class="octolize_dhl_rwd_error" role="alert" hidden></div>'
  return widget
}

function configurationError(message) {
  return `Błąd konfiguracji motywu RWD: ${message}.`
}

export function createRwdView({ root = document, selectors, customSelectorKeys = new Set() }) {
  if (!root.getElementById(STYLE_ID)) {
    const style = root.createElement('style')
    style.id = STYLE_ID
    style.textContent = styles
    root.head.appendChild(style)
  }
  if (!root.getElementById(MODAL_ID)) {
    const modal = root.createElement('div')
    modal.id = MODAL_ID
    modal.hidden = true
    modal.innerHTML = '<button type="button" class="octolize_dhl_rwd_close">Zamknij mapę</button><iframe allow="geolocation" title="Mapa punktów odbioru DHL"></iframe>'
    root.body.appendChild(modal)
  }

  const modal = () => root.getElementById(MODAL_ID)
  const widgets = () => [...root.querySelectorAll(`.${WIDGET_CLASS}`)]
  const detachedAddressContent = new Map()

  return {
    setPickupAddressMode(active) {
      if (active) {
        root.querySelectorAll(selectors.differentAddressContent).forEach(element => {
          const placeholder = root.createComment('DHL: pola innego adresu wysylki')
          element.replaceWith(placeholder)
          detachedAddressContent.set(placeholder, element)
        })
      } else {
        detachedAddressContent.forEach((element, placeholder) => placeholder.replaceWith(element))
        detachedAddressContent.clear()
      }
    },
    render({ stage, pickupPoint, requirePostnummer, onOpen, onPostnummer }) {
      if (!['address', 'summary'].includes(stage)) {
        widgets().forEach(widget => widget.remove())
        return { widget: null, configurationError: null }
      }
      const isAddressStage = stage === 'address'
      const form = root.querySelector(isAddressStage ? selectors.addressForm : selectors.summaryForm)
      const anchor = root.querySelector(isAddressStage ? selectors.addressAnchor : selectors.summaryAnchor)
      widgets().forEach(widget => {
        if (widget.parentElement !== anchor) widget.remove()
      })
      if (!form || !anchor || (isAddressStage && anchor !== form && anchor.closest('form') !== form)) {
        const label = isAddressStage ? 'kotwicy formularza adresowego' : 'kotwicy podsumowania'
        return { widget: null, configurationError: configurationError(`nie znaleziono ${label}`) }
      }

      let widget = anchor.querySelector(`.${WIDGET_CLASS}`)
      if (!widget) {
        widget = createWidget(root)
        anchor.appendChild(widget)
      }
      const button = widget.querySelector('.octolize_dhl_rwd_button')
      button.hidden = !isAddressStage
      button.onclick = isAddressStage ? onOpen : null
      widget.querySelector('.octolize_dhl_rwd_heading').textContent = isAddressStage
        ? 'Dane do wysyłki - punkt DHL' : 'Punkt odbioru DHL'
      widget.querySelector('.octolize_dhl_rwd_summary_hint').hidden = isAddressStage
      const identifier = pickupPointIdentifier(pickupPoint)
      button.textContent = identifier ? `Wybrany punkt: ${identifier}` : 'Wybierz punkt'
      const details = widget.querySelector('.octolize_dhl_rwd_details')
      details.hidden = !pickupPoint
      widget.querySelector('.octolize_dhl_rwd_name').textContent = formatPickupPointTitle(pickupPoint)
      widget.querySelector('.octolize_dhl_rwd_address').textContent = formatPickupPointAddress(pickupPoint)
      const postnummer = widget.querySelector('.octolize_dhl_rwd_postnummer')
      postnummer.hidden = !isAddressStage || !requirePostnummer
      const input = widget.querySelector('.octolize_dhl_rwd_postnummer_input')
      if (root.activeElement !== input) input.value = String(pickupPoint?.postnummer ?? '')
      input.oninput = onPostnummer
      input.onblur = onPostnummer
      input.disabled = !isAddressStage
      const postnummerSummary = widget.querySelector('.octolize_dhl_rwd_postnummer_summary')
      postnummerSummary.hidden = isAddressStage || !requirePostnummer
      postnummerSummary.textContent = requirePostnummer ? `Postnummer: ${pickupPoint?.postnummer ?? ''}` : ''
      const forwardButton = [...root.querySelectorAll(selectors.forwardButton)]
        .find(button => button.form === form)
      if (customSelectorKeys.has('forwardButton') && !forwardButton) {
        widget.remove()
        return { widget: null, configurationError: configurationError('nie znaleziono przycisku przejścia dalej') }
      }

      return { widget, configurationError: null }
    },
    removeWidgets() {
      widgets().forEach(widget => widget.remove())
    },
    getPickupField(fieldId) {
      try {
        return root.querySelector(getRwdPickupFieldSelector(selectors, fieldId))
      } catch {
        return null
      }
    },
    setPickupField(fieldId, value) {
      const field = this.getPickupField(fieldId)
      if (!field) return false
      if (field.value !== value) field.value = value
      return true
    },
    openMap(url) {
      const element = modal()
      const iframe = element?.querySelector('iframe')
      if (!element || !iframe) return null
      iframe.src = String(url)
      element.hidden = false
      return iframe
    },
    closeMap() {
      const element = modal()
      const iframe = element?.querySelector('iframe')
      if (iframe) iframe.src = ''
      if (element) element.hidden = true
    },
    isCloseTarget(target) {
      return Boolean(target?.closest?.('.octolize_dhl_rwd_close'))
    },
    showError(message) {
      const widgetError = root.querySelector(`.${WIDGET_CLASS} .octolize_dhl_rwd_error`)
      const form = root.querySelector(selectors.summaryForm) ?? root.querySelector(selectors.addressForm) ?? root.querySelector(selectors.basketForm)
      const container = form ?? root.body
      let error = widgetError ?? container?.querySelector('.octolize_dhl_rwd_error')
      if (!error && container) {
        error = root.createElement('div')
        error.className = 'octolize_dhl_rwd_error'
        error.setAttribute('role', 'alert')
        container.prepend(error)
      }
      if (error) {
        error.textContent = message
        error.hidden = false
      }
    },
    clearError() {
      root.querySelectorAll('.octolize_dhl_rwd_error').forEach(error => {
        error.hidden = true
        error.textContent = ''
      })
    },
  }
}
